import {
  skipToken,
  useQuery,
} from '@tanstack/react-query'
import backend from '@api/backend'
import { DeviceIDTypes } from '@data/device/model/device'
import { retryOnlyTransient } from '../../lib/react-query/retryPolicy'
import {
  mapReport30Days,
  Report30DaysMappingError,
} from './mapReport30Days'
import {
  Report30Days,
  Report30DaysAPI,
} from './model/report30Days.model'

export const REPORT_30_DAYS_QUERY_KEY = ['reports', '30-days'] as const

export interface ReportQuery {
  deviceId: string
  offPeakBaseline?: number
  offPeakStartHour?: number
  offPeakEndHour?: number
  projectedSaving: number
  uom: string
  idType: DeviceIDTypes
}

// The report is generated from a parameter form behind an explicit submit, so the query
// is keyed on the *submitted* parameters rather than on live form state — keying on the
// latter would refetch on every keystroke in the off-peak hour fields.
//
// Every parameter is part of the report's identity: changing uom or projectedSaving
// produces a different document, so the whole query object belongs in the key.
export const useReport30DaysQuery = (query: ReportQuery | typeof skipToken) => {
  return useQuery<Report30Days>({
    queryKey: [...REPORT_30_DAYS_QUERY_KEY, query === skipToken ? null : query],
    queryFn: query === skipToken
      ? skipToken
      : ({ signal }) => getReport30Days(query, signal),
    // The report reflects a moment in time and is cheap to regenerate on request. Serving
    // it stale would show yesterday's figures to someone who just pressed the button.
    staleTime: 0,
    // The report is generated by an explicit button press, so it must not regenerate on
    // its own. With staleTime 0 the default would refetch every time the tab regains
    // focus — alt-tabbing away and back silently re-ran the whole report.
    refetchOnWindowFocus: false,
    // A malformed payload is deterministic: the same request returns the same bytes and
    // fails the same way. The shared policy cannot see that, because a mapping error is
    // not an AxiosError — it has no `response.status`, so it reads as a transient fault
    // and burns all three retries before the user is told anything.
    retry: (failureCount, error) =>
      error instanceof Report30DaysMappingError
        ? false
        : retryOnlyTransient(failureCount, error),
  })
}

const getReport30Days = async (
  query: ReportQuery,
  signal: AbortSignal,
): Promise<Report30Days> => {
  const { deviceId, ...params } = query

  const response = await backend.get<Report30DaysAPI>(
    `/reports/${deviceId}/initial_30_day_report`,
    {
      signal,
      params,
    },
  )

  // Mapping inside the queryFn is what turns a malformed payload into a handled query
  // error rather than a TypeError thrown during a render.
  return mapReport30Days(query, response.data)
}
